import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Grid,
} from '@mui/material';
import {
  Search,
  Add,
  MoreVert,
  Edit,
  Delete,
  LocationOn,
  Devices,
  MyLocation,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  useSites,
  useCreateSite,
  useUpdateSite,
  useDeleteSite,
  useOrganizations,
} from '../../hooks/useFleet';
import type { Site } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';

// Fix Leaflet marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface SiteFormData {
  organization_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
  timezone: string;
}

const initialFormData: SiteFormData = {
  organization_id: '',
  name: '',
  address: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  latitude: null,
  longitude: null,
  timezone: 'America/New_York',
};

// Map click handler component
const MapClickHandler: React.FC<{
  onLocationSelect: (lat: number, lng: number) => void;
}> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const SitesPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [formData, setFormData] = useState<SiteFormData>(initialFormData);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  const { data, isLoading, error } = useSites({
    page: page + 1,
    size: rowsPerPage,
    organization_id: orgFilter,
  });

  const { data: orgsData } = useOrganizations();

  const createMutation = useCreateSite();
  const updateMutation = useUpdateSite();
  const deleteMutation = useDeleteSite();

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (site?: Site) => {
    setSubmitError(null);
    if (site) {
      setEditingSite(site);
      setFormData({
        organization_id: site.organization_id,
        name: site.name,
        address: site.address || '',
        city: site.city || '',
        state: site.state || '',
        country: site.country || '',
        postal_code: site.postal_code || '',
        latitude: site.latitude || null,
        longitude: site.longitude || null,
        timezone: site.timezone || 'America/New_York',
      });
    } else {
      setEditingSite(null);
      setFormData({
        ...initialFormData,
        // Use global filter if set, otherwise first org
        organization_id: orgFilter || orgsData?.data?.[0]?.id || '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSite(null);
    setFormData(initialFormData);
  };

  const handleInputChange = (field: keyof SiteFormData) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      latitude: Math.round(lat * 1000000) / 1000000,
      longitude: Math.round(lng * 1000000) / 1000000,
    }));
  };

  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleLocationSelect(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
        }
      );
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude || undefined,
        longitude: formData.longitude || undefined,
      };

      console.log('Submitting site data:', submitData);

      if (editingSite) {
        await updateMutation.mutateAsync({
          id: editingSite.id,
          data: submitData,
        });
      } else {
        await createMutation.mutateAsync(submitData);
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save site:', err);
      setSubmitError(err?.message || 'Failed to save site. Please try again.');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, site: Site) => {
    setAnchorEl(event.currentTarget);
    setSelectedSite(site);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSite(null);
  };

  const handleEditClick = () => {
    if (selectedSite) {
      handleOpenDialog(selectedSite);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedSite) {
      try {
        await deleteMutation.mutateAsync(selectedSite.id);
      } catch (err) {
        console.error('Failed to delete site:', err);
      }
    }
    setDeleteConfirmOpen(false);
    setSelectedSite(null);
  };

  const sites = data?.data || [];
  const totalCount = data?.total || 0;
  const organizations = orgsData?.data || [];

  const filteredSites = searchQuery
    ? sites.filter(
        (site) =>
          site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          site.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          site.address?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sites;

  const getOrgName = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    return org?.name || 'Unknown';
  };

  return (
    <Box sx={{ p: 3 }}>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.sites}
        actionButton={{
          label: 'New Site',
          icon: <Add />,
          onClick: () => handleOpenDialog(),
        }}
      />

      {organizations.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Create an organization first before adding sites.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load sites. Please try again.
        </Alert>
      )}

      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search sites..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#6366f1' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Site</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Coordinates</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Devices</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSites.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <LocationOn sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                          No sites found. Create your first site to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSites.map((site) => (
                      <TableRow key={site.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ color: '#6366f1' }} />
                            <Typography fontWeight={500}>{site.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={getOrgName(site.organization_id)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{site.address || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[site.city, site.state, site.country].filter(Boolean).join(', ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {site.latitude && site.longitude ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {site.latitude.toFixed(4)}, {site.longitude.toFixed(4)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not set
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Devices sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">{(site as any).device_count || 0}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, site)}>
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingSite ? 'Edit Site' : 'Create Site'}</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={formData.organization_id}
                    label="Organization"
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, organization_id: e.target.value as string }))
                    }
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Site Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  fullWidth
                  required
                  placeholder="e.g., Downtown Store"
                />
                <TextField
                  label="Address"
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  fullWidth
                  placeholder="e.g., 123 Main Street"
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="City"
                    value={formData.city}
                    onChange={handleInputChange('city')}
                    fullWidth
                  />
                  <TextField
                    label="State"
                    value={formData.state}
                    onChange={handleInputChange('state')}
                    sx={{ width: 120 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Country"
                    value={formData.country}
                    onChange={handleInputChange('country')}
                    fullWidth
                  />
                  <TextField
                    label="Postal Code"
                    value={formData.postal_code}
                    onChange={handleInputChange('postal_code')}
                    sx={{ width: 120 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    label="Latitude"
                    type="number"
                    value={formData.latitude || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        latitude: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    fullWidth
                    inputProps={{ step: 'any' }}
                  />
                  <TextField
                    label="Longitude"
                    type="number"
                    value={formData.longitude || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        longitude: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    fullWidth
                    inputProps={{ step: 'any' }}
                  />
                  <IconButton onClick={handleUseMyLocation} title="Use my location">
                    <MyLocation />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Click on map to set location:
              </Typography>
              <Box sx={{ height: 350, borderRadius: 1, overflow: 'hidden' }}>
                <MapContainer
                  center={[
                    formData.latitude || 39.8283,
                    formData.longitude || -98.5795,
                  ]}
                  zoom={formData.latitude ? 12 : 4}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler onLocationSelect={handleLocationSelect} />
                  {formData.latitude && formData.longitude && (
                    <Marker position={[formData.latitude, formData.longitude]} />
                  )}
                </MapContainer>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.organization_id ||
              !formData.name ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : editingSite ? (
              'Save Changes'
            ) : (
              'Create Site'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Site?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedSite?.name}</strong>? All devices at
            this site will be unassigned. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SitesPage;
